import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { companiesApi, businessStatesApi, exportApi } from '@/lib/api';
import { toast } from 'sonner';
import { Download, Mail, Building2, MapPin, Filter } from 'lucide-react';
import { motion } from 'framer-motion';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'Washington D.C.' },
];

export default function MailingListPage() {
  const [companies, setCompanies] = useState([]);
  const [businessStates, setBusinessStates] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [useBusinessStates, setUseBusinessStates] = useState(true);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [companiesRes, statesRes] = await Promise.all([
        companiesApi.getAll(),
        businessStatesApi.getAll(true),
      ]);
      setCompanies(companiesRes.data);
      setBusinessStates(statesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanies((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
  };

  const toggleState = (stateCode) => {
    setSelectedStates((prev) =>
      prev.includes(stateCode)
        ? prev.filter((code) => code !== stateCode)
        : [...prev, stateCode]
    );
  };

  const selectAllCompanies = () => {
    if (selectedCompanies.length === companies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(companies.map((c) => c.id));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      
      if (selectedCompanies.length > 0) {
        params.company_ids = selectedCompanies.join(',');
      }
      
      if (useBusinessStates) {
        params.business_states_only = true;
      } else if (selectedStates.length > 0) {
        params.states = selectedStates.join(',');
      }
      
      const response = await exportApi.mailingList(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mailing_list_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Mailing list downloaded');
    } catch (error) {
      toast.error('Failed to export mailing list');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mailing-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mailing List Export</h1>
          <p className="text-muted-foreground mt-1">
            Generate employee mailing lists for physical mail campaigns
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting}
          className="bg-[#E31837] hover:bg-[#c41530] text-white"
          data-testid="export-mailing-list"
        >
          {exporting ? (
            <div className="spinner w-4 h-4" />
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Export Mailing List
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-card border-border" data-testid="company-selection">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Select Companies
              </CardTitle>
              <CardDescription>
                Filter mailing list by specific companies
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllCompanies}
                  data-testid="select-all-companies"
                >
                  {selectedCompanies.length === companies.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {companies.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No companies available</p>
                ) : (
                  companies.map((company) => (
                    <div
                      key={company.id}
                      className="flex items-center space-x-3 p-2 rounded hover:bg-muted/50 transition-colors"
                    >
                      <Checkbox
                        id={`company-${company.id}`}
                        checked={selectedCompanies.includes(company.id)}
                        onCheckedChange={() => toggleCompany(company.id)}
                        data-testid={`company-checkbox-${company.id}`}
                      />
                      <label
                        htmlFor={`company-${company.id}`}
                        className="flex-1 text-sm cursor-pointer"
                      >
                        {company.name}
                        <span className="text-muted-foreground ml-2">
                          ({company.employee_count} employees)
                        </span>
                      </label>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {selectedCompanies.length === 0
                  ? 'All companies will be included'
                  : `${selectedCompanies.length} company(s) selected`}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* State Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border" data-testid="state-selection">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Select States
              </CardTitle>
              <CardDescription>
                Filter employees by state to only include states where you do business
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-center space-x-3 p-3 rounded-lg bg-muted/50">
                <Checkbox
                  id="useBusinessStates"
                  checked={useBusinessStates}
                  onCheckedChange={(checked) => {
                    setUseBusinessStates(checked);
                    if (checked) setSelectedStates([]);
                  }}
                  data-testid="use-business-states"
                />
                <label htmlFor="useBusinessStates" className="text-sm cursor-pointer">
                  <span className="font-medium">Use Business States Only</span>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {businessStates.length > 0
                      ? `Active: ${businessStates.map((s) => s.state_code).join(', ')}`
                      : 'No business states configured'}
                  </p>
                </label>
              </div>

              {!useBusinessStates && (
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-2">
                    {US_STATES.map((state) => (
                      <div
                        key={state.code}
                        className="flex items-center space-x-2 p-1"
                      >
                        <Checkbox
                          id={`state-${state.code}`}
                          checked={selectedStates.includes(state.code)}
                          onCheckedChange={() => toggleState(state.code)}
                          data-testid={`state-checkbox-${state.code}`}
                        />
                        <label
                          htmlFor={`state-${state.code}`}
                          className="text-sm cursor-pointer"
                        >
                          {state.code}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!useBusinessStates && (
                <p className="text-xs text-muted-foreground mt-3">
                  {selectedStates.length === 0
                    ? 'All states will be included'
                    : `${selectedStates.length} state(s) selected`}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Export Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Export Preview</h3>
                <p className="text-sm text-muted-foreground">
                  The exported Excel file will include: Name, Company, Address, City, State, Zip Code, Start Date, and Homeowner Status.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-muted text-sm">
                    <Filter className="w-3 h-3" />
                    Companies: {selectedCompanies.length === 0 ? 'All' : selectedCompanies.length}
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-muted text-sm">
                    <MapPin className="w-3 h-3" />
                    States: {useBusinessStates ? `Business (${businessStates.length})` : selectedStates.length === 0 ? 'All' : selectedStates.length}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
